import { SlDropdownMenu } from '@stackline/ui';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import useAtlasV2AdvancedSearchData from 'src/serverProxy/useAtlasV2AdvancedSearchData';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';

const groupByOptions = [
  { id: 'brandId', label: 'Brand', dataParseKey: 'brand' },
  { id: 'parentBrandId', label: 'Manufacturer', dataParseKey: 'parentBrand' }
];

/**
 *
 * MarketShare Sales Summary double dropdown
 */
const AtlasV2MarketShareDropdownTopEntityChart = () => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState('brandId');
  const currentGroupByOption =
    groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2AdvancedSearchData({
    fields: [{ name: 'retailSales' }],
    groupByField: 'retailerId',
    indexName: 'sales',
    requestId: `Atlas-retailSales- market`,
    shouldFetchComparison: true
  });

  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2AdvancedSearchData({
    fields: [{ name: 'retailSales' }],
    groupByField: selectedGroupByFieldId,
    indexName: 'sales',
    requestId: `Atlas-retailSales- target`,
    shouldFetchComparison: true
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isMarketShareLoading && !isTargetLoading && marketShareData && targetData) {
      const currentMarketShare = marketShareData?.[0]?.values.retailSales_sum_value ?? 0;
      const compareMarketShare = marketShareData?.[0]?.comparisonValues.retailSales_sum_value ?? 0;

      targetData.forEach((datum) => {
        const categoryObj =
          datum?.metadata?.[currentGroupByOption.dataParseKey] ??
          datum?.comparisonMetadata?.[currentGroupByOption.dataParseKey];
        const formattedCategoryObj = {
          name: categoryObj?.name,
          id: categoryObj?.id,
          type: currentGroupByOption?.dataParseKey?.toLocaleLowerCase()
        };

        const responseKey = 'retailSales_sum_value';
        const currentDatum = datum?.values?.[responseKey] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[responseKey] ?? 0;

        if (!categoryObj || !categoryObj.name) {
          return;
        }

        comparisonData.push([categoryObj.name, safeDivide(comparisonDatum, compareMarketShare)]);
        currentData.push([categoryObj.name, safeDivide(currentDatum, currentMarketShare)]);
        categoryData.push(formattedCategoryObj);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [currentGroupByOption, isMarketShareLoading, isTargetLoading, marketShareData, targetData]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Category Market Share by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Retail Sales - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isMarketShareLoading || isTargetLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center">
          <Text variant="h4">Market Share </Text>
          <Flex marginLeft={1}>
            <Text variant="h4"> by</Text>
          </Flex>

          <SlDropdownMenu
            buttonVariant="title"
            options={groupByOptions}
            selectedId={selectedGroupByFieldId}
            onChange={(option) => setSelectedGroupByFieldId(option.id)}
            defaultLabel="Brand"
            open={false}
          />
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2MarketShareDropdownTopEntityChart;
